'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, } from 'react';
import { cn } from '../../lib/utils.js';
var useAutosizeTextArea = function (_a) {
    var textAreaRef = _a.textAreaRef, triggerAutoSize = _a.triggerAutoSize, _b = _a.maxHeight, maxHeight = _b === void 0 ? Number.MAX_SAFE_INTEGER : _b, _c = _a.minHeight, minHeight = _c === void 0 ? 0 : _c;
    var _d = useState(true), init = _d[0], setInit = _d[1];
    useEffect(function () {
        // We need to reset the height momentarily to get the correct scrollHeight for the textarea
        var offsetBorder = 6;
        var textAreaElement = textAreaRef.current;
        if (textAreaElement) {
            if (init) {
                textAreaElement.style.minHeight = "".concat(minHeight + offsetBorder, "px");
                if (maxHeight > minHeight) {
                    textAreaElement.style.maxHeight = "".concat(maxHeight, "px");
                }
                setInit(false);
            }
            textAreaElement.style.height = "".concat(minHeight + offsetBorder, "px");
            var scrollHeight = textAreaElement.scrollHeight;
            // We then set the height directly, outside of the render loop
            // Trying to set this with state or a ref will product an incorrect value.
            if (scrollHeight > maxHeight) {
                textAreaElement.style.height = "".concat(maxHeight, "px");
            }
            else {
                textAreaElement.style.height = "".concat(scrollHeight + offsetBorder, "px");
            }
        }
    }, [textAreaRef, triggerAutoSize, init, minHeight, maxHeight]);
};
export var AutosizeTextarea = forwardRef(function (_a, ref) {
    var _b = _a.maxHeight, maxHeight = _b === void 0 ? Number.MAX_SAFE_INTEGER : _b, _c = _a.minHeight, minHeight = _c === void 0 ? 52 : _c, className = _a.className, onChange = _a.onChange, value = _a.value, props = __rest(_a, ["maxHeight", "minHeight", "className", "onChange", "value"]);
    var textAreaRef = useRef(null);
    var _d = useState(''), triggerAutoSize = _d[0], setTriggerAutoSize = _d[1];
    useAutosizeTextArea({
        textAreaRef: textAreaRef,
        triggerAutoSize: triggerAutoSize,
        maxHeight: maxHeight,
        minHeight: minHeight,
    });
    useImperativeHandle(ref, function () { return ({
        textArea: textAreaRef.current,
        focus: function () { var _a; return (_a = textAreaRef === null || textAreaRef === void 0 ? void 0 : textAreaRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
        maxHeight: maxHeight,
        minHeight: minHeight,
    }); });
    useEffect(function () {
        setTriggerAutoSize(value);
    }, [props === null || props === void 0 ? void 0 : props.defaultValue, value]);
    return (<textarea {...props} value={value} ref={textAreaRef} className={cn('border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/70 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex w-full rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm', className)} onChange={function (e) {
            setTriggerAutoSize(e.target.value);
            onChange === null || onChange === void 0 ? void 0 : onChange(e);
        }}/>);
});
AutosizeTextarea.displayName = 'AutosizeTextarea';
